<template>
  <ColumnChart
    ref="chart"
    :unit="unit"
    :scale="scale"
    :title="title"
    :loading="loading"
  />
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ColumnChart from '../ColumnChart.vue';

export default {
  name: 'OEELive',
  props: {
    title: {
      type: String,
      default: '',
    },
    scale: {
      type: Number,
      default: 1,
    },
    unit: {
      type: String,
      default: '',
    },
  },
  components: {
    ColumnChart,
  },
  data() {
    return {
      loading: false,
      machinenames: [],
      interval: null,
    };
  },
  computed: {
    ...mapState('cellwiseProductionDashboard', [
      'machines',
      'serverTime',
      'dataFromJSON',
      'intervalfetch',
      'config',
      'currentShift',
      'currentDate',
      'shifts',
    ]),
    ...mapGetters('cellwiseProductionDashboard', [
      'shiftStartTime',
    ]),
    cellname() {
      return this.$route.query.selectedcell;
    },
  },
  watch: {
    '$route.query.selectedcell': {
      async handler() {
        if (!this.dataFromJSON) {
          await this.fetchData();
        } else if (this.dummydata) {
          await this.dummydata();
        }
      },
    },
    async currentDate() {
      if (!this.dataFromJSON) await this.fetchData();
    },
    async currentShift() {
      if (!this.dataFromJSON) await this.fetchData();
    },
  },
  async mounted() {
    if (this.dataFromJSON) {
      await this.dummydata();
    } else {
      await this.fetchData();
      const delay = (this.intervalfetch || 25) * 1000;
      this.interval = setInterval(async () => {
        await this.fetchData();
      }, delay);
    }
  },
  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  },
  methods: {
    ...mapActions('cellwiseProductionDashboard', ['getMachines', 'getReports', 'getFileData']),

    async dummydata() {
      try {
        const res = await this.getFileData('jsondata/oeelive.json');
        if (res) {
          this.machinenames = res.machinenames || [];
          this.setOptions({ reportData: res.reportdata });
        }
      } catch (e) {
        this.setOptions([]);
      }
    },

    async fetchData() {
      if (this.loading) return;
      this.loading = true;
      try {
        if (!this.machines || this.machines.length === 0) {
          await this.getMachines();
        }

        let filteredMachines = this.machines || [];
        if (this.cellname) {
          filteredMachines = filteredMachines.filter((m) => m.machinecell === this.cellname);
        }
        this.machinenames = filteredMachines.length > 0
          ? filteredMachines.map((m) => m.machinename).sort()
          : ['-'];

        let activeSite = this.$store.state.user?.activeSite
         || this.$store.state.auth?.user?.site_id;
        if (!activeSite) {
          try {
            const swxUser = JSON.parse(localStorage.getItem('user') || '{}');
            activeSite = swxUser?.site_id || swxUser?.site?.id || swxUser?.customer?.site_id;
          } catch (e) { /* ignore */ }
        }
        if (!activeSite && this.machines?.length > 0) {
          activeSite = this.machines[0].siteId || this.machines[0].siteid;
        }

        if (!activeSite) {
          this.loading = false;
          return;
        }

        // Date Format Conversion
        let yyyymmddDate = this.currentDate;
        if (!this.currentDate || String(this.currentDate).length > 8) {
          const dateObj = this.currentDate ? new Date(this.currentDate) : new Date();
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          yyyymmddDate = parseInt(`${year}${month}${day}`, 10);
        }

        if (!this.currentShift) {
          this.loading = false;
          return;
        }

        const payload = {
          siteid: activeSite,
          dateVal: yyyymmddDate,
          shiftVal: this.currentShift,
          all: true,
        };

        if (this.cellname) payload.machinecell = this.cellname;

        const reportName = 'shiftliveshopfloor';
        const result = await this.getReports({ reportName, payload });

        if (result) {
          const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
          let finalData = parsedResult.reportData ? parsedResult.reportData : parsedResult;
          if (typeof finalData === 'string' && finalData !== '') {
            finalData = JSON.parse(finalData);
          }

          if (finalData && finalData.machines) {
            this.setOptions(finalData.machines);
          } else {
            this.setOptions([]);
          }
        } else {
          this.setOptions([]);
        }
      } catch (e) {
        this.setOptions([]);
      } finally {
        this.loading = false;
      }
    },

    setOptions(data) {
      const categories = [];
      const seriesData = [];

      let payloadArray = [];
      if (data && data.reportData) {
        payloadArray = data.reportData;
      } else if (Array.isArray(data)) {
        payloadArray = data;
      }

      let computedWorkingTime = 28800000;
      if (this.serverTime && this.shiftStratTime) {
        computedWorkingTime = this.serverTime - this.shiftStartTime;
      }
      if (computedWorkingTime <= 0) {
        computedWorkingTime = this.serverTime - (this.shiftStartTime - 86400000);
      }
      if (computedWorkingTime <= 0) {
        computedWorkingTime = 28800000;
      }

      this.machinenames.forEach((machine) => {
        categories.push(machine);
        let targetqty = 0;
        let actualqty = 0;
        let rejectionquantity = 0;
        let runtime = 0;
        let availabletime = 0;

        payloadArray.forEach((d) => {
          if (machine === d.machinename || machine === d.machine_name
           || machine === d.machinecode) {
            let mQty = 0;
            let mRejection = 0;
            let mApiTimeTarget = 0;

            if (d.production && d.production.length > 0) {
              mQty = d.production.reduce((sum, p) => sum + (p.produced || 0), 0);
              mRejection = d.production.reduce((sum, p) => sum + (p.rejected || 0), 0);
              mApiTimeTarget = d.production.reduce((sum, p) => sum + (p.timeTarget || 0), 0);
            } else {
              mQty = d.qty || d.actualquantity || 0;
              mRejection = d.rqty || d.rejectionquantity || 0;
              mApiTimeTarget = d.stdproduction || 0;
            }

            let mTarget = 0;
            const mPerformance = d.performance || 0;

            if (mPerformance > 0) {
              mTarget = mQty / (mPerformance / 100);
            } else {
              mTarget = mApiTimeTarget;
            }

            targetqty += Math.round(mTarget);
            actualqty += mQty;
            rejectionquantity += mRejection;
            runtime += d.runtime || 0;
            availabletime += d.workingTime
              || d.availabletime || d.shiftAvailableTime || computedWorkingTime;
          }
        });

        let a = 0;
        let p = 0;
        let q = 0;

        if (availabletime > 0) a = (runtime / availabletime) * 100;
        if (targetqty > 0) p = (actualqty / targetqty) * 100;
        if (actualqty > 0) q = ((actualqty - rejectionquantity) / actualqty) * 100;

        let oee = 0;
        if (a > 0 && p > 0 && q > 0) {
          const oeeinper = (a * p * q) / 10000;
          oee = Math.round(oeeinper * 100) / 100;
        }

        // --- Colors ---
        const safeColor = this.config.colors?.safe || '#21CE99';
        const warningColor = this.config.colors?.warning || '#FFDA6B';
        const dangerColor = this.config.colors?.danger || '#ED5353';

        let color = warningColor;
        if (oee > (this.config.greaterthangreen || 75)) color = safeColor;
        if (oee < (this.config.lessthanred || 50) || oee === 0) color = dangerColor;

        seriesData.push({ color, y: oee });
      });

      const plotOptions = {
        series: {
          borderWidth: 0,
          groupPadding: 0,
          dataLabels: {
            enabled: true,
            inside: true,
          },
        },
      };

      if (this.$refs.chart) {
        this.$refs.chart.assignData({
          categories,
          series: [{ name: 'OEE', data: seriesData }],
          plotOptions,
        });
      }
    },
  },
};
</script>
